import {ApiFailure} from '@/lib/api/client';
import {OUTREACH_DEMOS} from '@/lib/demo/media';
import {fromZonedDayMinutes} from '@/lib/time/tz';
import type {
  ApiAssistantReply,
  ApiAvailabilityResponse,
  ApiBooking,
  ApiCreateBookingResponse,
  ApiCustomer,
  ApiService,
  ApiTenant,
  ApiWork,
  InfoCard,
} from '@/types/api';

/**
 * Демо-студии для рассылки работают без базы: данные берутся из business.json прямо в сборке,
 * а запись хранится только в браузере. Так страница открывается там, где сервер базы недоступен
 * (часть российских провайдеров режет зарубежные адреса), и реальным студиям ничего не приходит.
 */
interface BusinessFile {
  slug: string;
  name: string;
  tagline?: string;
  description?: string;
  accent?: string;
  timezone: string;
  currency: string;
  locale: string;
  phone?: string;
  address?: string;
  mapUrl?: string;
  bookingLeadMinutes: number;
  cancelWindowMinutes: number;
  slotStepMinutes: number;
  resources: {name: string; kind: string}[];
  services: {
    name: string;
    description?: string;
    price: number;
    durationMinutes: number;
    bufferBeforeMinutes: number;
    bufferAfterMinutes: number;
    resourceKind?: string;
  }[];
  hours: Record<string, {start: string; end: string}[]>;
  infoCards: {title: string; body: string; icon?: string}[];
}

const files = import.meta.glob<BusinessFile>('../../../tenants/*/business.json', {eager: true, import: 'default'});

const byslug = new Map<string, BusinessFile>();
for (const file of Object.values(files)) {
  if ((OUTREACH_DEMOS as readonly string[]).includes(file.slug)) byslug.set(file.slug, file);
}

const WEEK = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const DAY_LABEL: Record<string, string> = {mon: 'Пн', tue: 'Вт', wed: 'Ср', thu: 'Чт', fri: 'Пт', sat: 'Сб', sun: 'Вс'};
const ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export function hasStaticDemo(slug: string): boolean {
  return byslug.has(slug);
}

function need(slug: string): BusinessFile {
  const business = byslug.get(slug);
  if (!business) throw new ApiFailure('Студия не найдена.', 'not_found', 404);
  return business;
}

function toMinutes(value: string): number {
  const [h, m] = value.split(':').map((part) => Number.parseInt(part, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}

function hoursSummary(hours: BusinessFile['hours']): string[] {
  const lines: string[] = [];
  let i = 0;
  while (i < ORDER.length) {
    const key = ORDER[i] as string;
    const windows = hours[key];
    if (!windows || windows.length === 0) {
      i += 1;
      continue;
    }
    const label = windows.map((w) => `${w.start}–${w.end}`).join(', ');
    let j = i;
    while (j + 1 < ORDER.length && JSON.stringify(hours[ORDER[j + 1] as string]) === JSON.stringify(windows)) j += 1;
    const from = DAY_LABEL[key] ?? key;
    const to = DAY_LABEL[ORDER[j] as string] ?? key;
    lines.push(`${i === j ? from : `${from}–${to}`}: ${label}`);
    i = j + 1;
  }
  return lines;
}

function servicesOf(business: BusinessFile): ApiService[] {
  return business.services.map((service, index) => ({
    id: `s${index + 1}`,
    name: service.name,
    description: service.description ?? null,
    priceMinor: Math.round(service.price * 100),
    currency: business.currency,
    durationMinutes: service.durationMinutes,
    bufferBeforeMinutes: service.bufferBeforeMinutes,
    bufferAfterMinutes: service.bufferAfterMinutes,
    resourceKind: service.resourceKind ?? null,
    sort: index + 1,
  }));
}

export function staticCatalog(slug: string): {tenant: ApiTenant; services: ApiService[]; works: ApiWork[]; serverTime: string} {
  const business = need(slug);
  const services = servicesOf(business);
  const infoCards: InfoCard[] = business.infoCards.map((card, index) => ({
    id: `c${index + 1}`,
    title: card.title,
    body: card.body,
    icon: card.icon ?? null,
  }));
  return {
    tenant: {
      slug: business.slug,
      name: business.name,
      tagline: business.tagline ?? null,
      description: business.description ?? null,
      accent: business.accent ?? null,
      timezone: business.timezone,
      currency: business.currency,
      locale: business.locale,
      status: 'preview',
      phone: business.phone ?? null,
      address: business.address ?? null,
      mapUrl: business.mapUrl ?? null,
      heroImageUrl: null,
      logoUrl: null,
      social: {},
      bookingLeadMinutes: business.bookingLeadMinutes,
      cancelWindowMinutes: business.cancelWindowMinutes,
      slotStepMinutes: business.slotStepMinutes,
      hoursSummary: hoursSummary(business.hours),
      serviceCount: services.length,
      resourceCount: business.resources.length,
      minPriceMinor: services.reduce((min, s) => Math.min(min, s.priceMinor), Number.MAX_SAFE_INTEGER),
      infoCards,
    },
    services,
    works: [],
    serverTime: new Date().toISOString(),
  };
}

function hash(text: string): number {
  let value = 0;
  for (let i = 0; i < text.length; i += 1) value = (value * 31 + text.charCodeAt(i)) % 1_000_003;
  return value;
}

function addDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map((part) => Number.parseInt(part, 10));
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days));
  return date.toISOString().slice(0, 10);
}

export function staticAvailability(slug: string, serviceId: string, fromDate: string, days: number): ApiAvailabilityResponse {
  const business = need(slug);
  const service = servicesOf(business).find((s) => s.id === serviceId) ?? servicesOf(business)[0];
  if (!service) throw new ApiFailure('Услуга не найдена.', 'not_found', 404);
  const earliest = Date.now() + business.bookingLeadMinutes * 60_000;
  const step = Math.max(15, business.slotStepMinutes);
  const result: ApiAvailabilityResponse['days'] = [];
  for (let offset = 0; offset < days; offset += 1) {
    const date = addDays(fromDate, offset);
    const [y, m, d] = date.split('-').map((part) => Number.parseInt(part, 10));
    const weekday = WEEK[new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()] ?? 'mon';
    const windows = business.hours[weekday] ?? [];
    const slots: string[] = [];
    for (const window of windows) {
      const start = toMinutes(window.start);
      const end = toMinutes(window.end);
      const needed = Math.min(service.durationMinutes, end - start);
      for (let minute = start; minute + needed <= end; minute += step) {
        if (service.durationMinutes > end - start && minute !== start) continue;
        const instant = fromZonedDayMinutes(date, minute, business.timezone);
        if (instant.getTime() < earliest) continue;
        if (hash(`${slug}${date}${minute}${service.id}`) % 5 === 0) continue;
        slots.push(instant.toISOString());
      }
    }
    result.push({date, isClosed: windows.length === 0, slots});
  }
  return {timezone: business.timezone, serviceId: service.id, durationMinutes: service.durationMinutes, days: result};
}

const STORE_PREFIX = 'demo-booking:';

function icsFor(booking: ApiBooking): string {
  const stamp = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d+/, '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `UID:${booking.id}@demo`,
    `DTSTART:${stamp(booking.startAt)}`,
    `DTEND:${stamp(booking.endAt)}`,
    `SUMMARY:${booking.serviceName}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`;
}

function save(token: string, booking: ApiBooking): void {
  try {
    localStorage.setItem(STORE_PREFIX + token, JSON.stringify(booking));
  } catch {
    /* хранилище недоступно: запись живёт только до перезагрузки */
  }
}

function load(token: string | undefined): ApiBooking {
  if (token) {
    try {
      const raw = localStorage.getItem(STORE_PREFIX + token);
      if (raw) return JSON.parse(raw) as ApiBooking;
    } catch {
      /* ниже вернётся ошибка */
    }
  }
  throw new ApiFailure('Запись не найдена.', 'not_found', 404);
}

export function staticCreateBooking(input: {
  slug: string;
  serviceId: string;
  startAt: string;
  customer: ApiCustomer;
}): ApiCreateBookingResponse {
  const business = need(input.slug);
  const service = servicesOf(business).find((s) => s.id === input.serviceId) ?? servicesOf(business)[0];
  if (!service) throw new ApiFailure('Услуга не найдена.', 'not_found', 404);
  const start = new Date(input.startAt);
  const end = new Date(start.getTime() + service.durationMinutes * 60_000);
  const token = `demo-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  const booking: ApiBooking = {
    id: token,
    tenantSlug: business.slug,
    status: 'confirmed',
    serviceName: service.name,
    customerName: input.customer.name,
    customerPhone: input.customer.phone,
    car: input.customer.car,
    comment: input.customer.comment,
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    durationMinutes: service.durationMinutes,
    priceMinor: service.priceMinor,
    currency: business.currency,
    timezone: business.timezone,
    address: business.address ?? null,
    phone: business.phone ?? null,
    resourceName: business.resources[0]?.name ?? null,
    canCancel: true,
    icsUrl: '',
  };
  booking.icsUrl = icsFor(booking);
  save(token, booking);
  return {booking, accessToken: token, replayed: false};
}

export function staticGetBooking(token: string | undefined): {booking: ApiBooking} {
  return {booking: load(token)};
}

export function staticCancelBooking(token: string | undefined): {booking: ApiBooking} {
  const booking = {...load(token), status: 'cancelled' as const, canCancel: false};
  if (token) save(token, booking);
  return {booking};
}

export function staticRescheduleBooking(token: string | undefined, startAt: string): {booking: ApiBooking} {
  const current = load(token);
  const start = new Date(startAt);
  const booking: ApiBooking = {
    ...current,
    startAt: start.toISOString(),
    endAt: new Date(start.getTime() + current.durationMinutes * 60_000).toISOString(),
  };
  booking.icsUrl = icsFor(booking);
  if (token) save(token, booking);
  return {booking};
}

export function staticAssistant(slug: string): ApiAssistantReply {
  const business = need(slug);
  return {
    reply: `Это демонстрационная версия: в рабочей версии помощник отвечает по данным студии «${business.name}».`,
    intent: 'unknown',
    usedTools: [],
    suggestions: ['Когда ближайшее окно?', 'Сколько стоит услуга?', 'Как найти студию?'],
  };
}
